import { Injectable, OnDestroy } from '@angular/core';
import { Observable, Subject, Subscription } from 'rxjs/Rx';
import { NetViewTreeDto, NetViewDto } from 'UvExplorerServer.Dtos.TS/NetViews';
import { AgentDto } from "UvExplorerServer.Dtos.TS/Agents";
import { ScheduledDiscovery } from 'DiscoveryEngine.TS/Discovery';
import { ScheduledDiscoveryResultDto } from 'UvExplorerServer.Dtos.TS/Discovery';
import { NetFacadeService } from '../services/netfacade.service';
import { DiscResultsService } from 'discovery';
import { List } from 'DiscoveryEngine.TS/Utils';
import { Router, NavigationEnd, NavigationSkipped } from '@angular/router';
import { MessageService } from 'base';
import { IdentityService } from 'identity';
import { DateService } from 'base';
import { UserClass } from '../../UvExplorerServer.Dtos.TS';

export enum NavType {
	None = 0,
	Network = 1,
	Agent = 2,
	Discovery = 3,
	DiscResult = 4,
	Account = 5,
	Dashboard = 6,
	Configuration = 7,
	Login = 8,
	Admin = 9,
	//	Tools = 10,
};

export class NavItem {
	public type: NavType;
	public levelIndex: number;
	public guid: string;
	public label: string;
	public selected: boolean;
	public tag: NetViewDto | AgentDto | ScheduledDiscovery | ScheduledDiscoveryResultDto;

	constructor() {
		this.type = NavType.None;
		this.levelIndex = -1;
		this.guid = "";
		this.label = "";
		this.selected = false;
		this.tag = null;
	}
}

export class NavLevel {
	public type: NavType;
	public index: number;
	public title: string;
	public items: NavItem[];

	constructor() {
		this.type = NavType.None;
		this.index = -1;
		this.title = "";
		this.items = [];
	}
}

export class NavLocation {

	public static DefaultType: NavType = NavType.Configuration; // NavType.Dashboard

	public type: NavType;
	public netViewGuid: string;
	public agentGuid: string;
	public schedDiscGuid: string;
	public schedDiscRsltGuid: string;
	public context: string;

	constructor(_type?: NavType) {
		this.type = ((_type === undefined) ? NavType.None : _type);
		this.netViewGuid = "";
		this.agentGuid = "";
		this.schedDiscGuid = "";
		this.schedDiscRsltGuid = "";
		this.context = "";
	}

	public get hasType(): boolean {
		return (this.type != NavType.None);
	}

	public get hasNetViewGuid(): boolean {
		return !!this.netViewGuid;
	}

	public get hasAgentGuid(): boolean {
		return !!this.agentGuid;
	}

	public get hasSchedDiscGuid(): boolean {
		return !!this.schedDiscGuid;
	}

	public get hasSchedDiscRsltGuid(): boolean {
		return !!this.schedDiscRsltGuid;
	}

	public get hasContext(): boolean {
		return !!this.context;
	}

	public deriveType(): void {
		this.type = NavLocation.DefaultType;

		if (this.hasNetViewGuid) {
			this.type = NavType.Network;

			if (this.hasAgentGuid) {
				this.type = NavType.Agent;

				if (this.hasSchedDiscGuid) {
					this.type = NavType.Discovery;

					if (this.hasSchedDiscRsltGuid) {
						this.type = NavType.DiscResult;
					}
				}
			}
		}
	}

	public equals(other: NavLocation): boolean {
		return (this.type === other.type &&
			this.netViewGuid === other.netViewGuid &&
			this.agentGuid === other.agentGuid &&
			this.schedDiscGuid === other.schedDiscGuid &&
			this.schedDiscRsltGuid === other.schedDiscRsltGuid);
	}
}

// NOTE: This service should implement OnDestroy, but a bug in the Angular 2 router
// prevents this from working.  When using a later version of the Angular router
// uncomment the OnDestroy implementation and see if it works.
@Injectable()
export class NavigationService implements OnDestroy {

	public levels: NavLevel[];
	public location: NavLocation;

	private levelsChanged: Subject<any>;
	public onLevelsChanged: Observable<any>;

	private locationChanged: Subject<any>;
	public onLocationChanged: Observable<any>;

	private doingSelect: boolean;

	private routerEventsSub: Subscription;
	private netViewTreeLoadedSub_1: Subscription;
	private netViewTreeLoadedSub_2: Subscription;
	private netViewTreeChangedSub: Subscription;
	private sharesLoadedSub_1: Subscription;
	private sharesLoadedSub_2: Subscription;
	private sharesChangedSub: Subscription;

	private ignoreRouterEvents: number;

	public navDestination: string;
	private navSkipped: boolean;

	public get sessionGuid(): string {
		return this.identityService.sessionGuid;
	}

	constructor(private identityService: IdentityService,
		private netFacadeService: NetFacadeService,
		private discResultsService: DiscResultsService,
		private messageService: MessageService,
		private router: Router,
		private dateService: DateService) {

		// Needed to avoid circular dependency between IdentityService and NavigationService
		IdentityService.gotoNavDestination = () => this.gotoNavDestination();
		IdentityService.gotoLogin = () => this.selectUrl("/login");

		this.ignoreRouterEvents = 0;
		this.navDestination = "";
		this.navSkipped = false;

		this.routerEventsSub = this.router.events.subscribe((event) => {
			console.assert(this.ignoreRouterEvents >= 0);
			this.navSkipped = false;
			if (event instanceof NavigationSkipped) {
				this.navSkipped = true;
			}
			else if (event instanceof NavigationEnd) {
				if (this.ignoreRouterEvents == 0) {
					let url: string = event.urlAfterRedirects;
					this.onRouteChange(url);
					if (!this.netFacadeService.isNetViewTreeLoaded && !this.netViewTreeLoadedSub_1) {
						this.netViewTreeLoadedSub_1 = this.netFacadeService.onNetViewTreeLoaded.subscribe(
							(result: boolean) => {
								this.netViewTreeLoadedSub_1.unsubscribe();
								this.netViewTreeLoadedSub_1 = undefined;
								if (this.netFacadeService.areSharesLoaded) {
									this.onRouteChange(url);
								}
							},
							(error?) => {
								this.netViewTreeLoadedSub_1.unsubscribe();
								this.netViewTreeLoadedSub_1 = undefined;
							}
						);
					}
					if (!this.netFacadeService.areSharesLoaded && !this.sharesLoadedSub_1) {
						this.sharesLoadedSub_1 = this.netFacadeService.onSharesLoaded.subscribe(
							(result: boolean) => {
								this.sharesLoadedSub_1.unsubscribe();
								this.sharesLoadedSub_1 = undefined;
								if (this.netFacadeService.isNetViewTreeLoaded) {
									this.onRouteChange(url);
								}
							},
							(error?) => {
								this.sharesLoadedSub_1.unsubscribe();
								this.sharesLoadedSub_1 = undefined;
							}
						);
					}
				}
			}
		});

		this.levels = [];
		this.location = new NavLocation(NavType.None);

		this.levelsChanged = new Subject<any>();
		this.onLevelsChanged = this.levelsChanged.asObservable();

		this.locationChanged = new Subject<any>();
		this.onLocationChanged = this.locationChanged.asObservable();

		this.doingSelect = false;

		let isInitialTreeLoad: boolean = !netFacadeService.isNetViewTreeLoaded;
		this.netViewTreeLoadedSub_2 = netFacadeService.onNetViewTreeLoaded.subscribe(
			(netViewTree: NetViewTreeDto) => {
				if (!isInitialTreeLoad) {
					this.initialize();
				}
				else {
					isInitialTreeLoad = false;
				}
			});
		this.netViewTreeChangedSub = netFacadeService.onNetViewTreeChanged.subscribe(
			(result: any) => {
				this.initialize();
			});

		let isInitialSharesLoad: boolean = !netFacadeService.areSharesLoaded;
		this.sharesLoadedSub_2 = netFacadeService.onSharesLoaded.subscribe(
			(result: any) => {
				if (!isInitialSharesLoad) {
					this.initialize();
				}
				else {
					isInitialSharesLoad = false;
				}
			});
		this.sharesChangedSub = netFacadeService.onSharesChanged.subscribe(
			(result: any) => {
				this.initialize();
			});

		//this.initialize();
	}

	public ngOnDestroy(): void {
		if (this.routerEventsSub) {
			this.routerEventsSub.unsubscribe();
			this.routerEventsSub = undefined;
		}
		if (this.netViewTreeLoadedSub_1) {
			this.netViewTreeLoadedSub_1.unsubscribe();
			this.netViewTreeLoadedSub_1 = undefined;
		}
		if (this.netViewTreeLoadedSub_2) {
			this.netViewTreeLoadedSub_2.unsubscribe();
			this.netViewTreeLoadedSub_2 = undefined;
		}
		if (this.netViewTreeChangedSub) {
			this.netViewTreeChangedSub.unsubscribe();
			this.netViewTreeChangedSub = undefined;
		}
		if (this.sharesLoadedSub_1) {
			this.sharesLoadedSub_1.unsubscribe();
			this.sharesLoadedSub_1 = undefined;
		}
		if (this.sharesLoadedSub_2) {
			this.sharesLoadedSub_2.unsubscribe();
			this.sharesLoadedSub_2 = undefined;
		}
		if (this.sharesChangedSub) {
			this.sharesChangedSub.unsubscribe();
			this.sharesChangedSub = undefined;
		}
	}

	// Check for user entering the application directly with a bookmarked link
	private loginRegex = /^\/login(\/failed)?$/;
	private adminRegex = /^\/admin((\/userlist\/createuser\/googleworkspace)|(\/userlist\/edituser\/googleworkspace\/.+)|(\/userlist\/loginadmin\/failed)|(\/grouplist\/creategroup\/googleworkspace)|(\/grouplist\/editgroup\/googleworkspace\/.+)|(\/grouplist\/syncgroups\/googleworkspace)|(\/grouplist\/loginadmin\/failed)|(\/authsettings\/save\/googleworkspace)|(\/authsettings\/loginadmin\/failed))?$/;
	//private toolsRegex = /^\/tools/;
	private accountRegex = /^\/account/;
	private dashboardRegex = /^\/home$/;
	private configurationRegex = /^\/networks/;
	private networkRegex = /^\/network\/(.+)$/;
	private agentRegex = /^\/agent\/(.+)$/;
	private discoveryRegex = /^\/discovery\/(.+)$/;
	private discresultRegex = /^\/discresult\/(.+)$/;

	private onRouteChange(url: string): void {

		if (this.identityService.isLoggedIn) {
			if (url.startsWith("/login")) {
				url = "/";
			}
		}
		else {
			if (url !== "/login/failed") {
				this.navDestination = url;
				url = "/login";
			}
		}

		if (url != this.getLocationUrl()) {
			let result: RegExpExecArray = null;
			if ((result = this.loginRegex.exec(url)) !== null) {
				let loc = new NavLocation(NavType.Login);
				if (result[1]) {
					let context = result[1];
					if (context === "/failed") {
						loc.context = context;
					}
				}
				this.selectLocation(loc);
			}
			else if ((result = this.adminRegex.exec(url)) !== null) {
				let loc = new NavLocation(NavType.Admin);
				if (result[1]) {
					let context = result[1];
					if (context === "/userlist/createuser/googleworkspace") {
						loc.context = context;
					}
					else if (context.startsWith("/userlist/edituser/googleworkspace/")) {
						loc.context = context;
					}
					else if (context === "/userlist/loginadmin/failed") {
						loc.context = context;
					}
					else if (context === "/grouplist/creategroup/googleworkspace") {
						loc.context = context;
					}
					else if (context.startsWith("/grouplist/editgroup/googleworkspace/")) {
						loc.context = context;
					}
					else if (context === "/grouplist/syncgroups/googleworkspace") {
						loc.context = context;
					}
					else if (context === "/grouplist/loginadmin/failed") {
						loc.context = context;
					}
					else if (context === "/authsettings/save/googleworkspace") {
						loc.context = context;
					}
					else if (context === "/authsettings/loginadmin/failed") {
						loc.context = context;
					}
				}
				this.selectLocation(loc);
			}
			//else if ((result = this.toolsRegex.exec(url)) !== null) {
			//	let loc = new NavLocation(NavType.Tools);
			//	this.selectLocation(loc);
			//}
			else if ((result = this.accountRegex.exec(url)) !== null) {
				let loc = new NavLocation(NavType.Account);
				this.selectLocation(loc);
			}
			else if ((result = this.dashboardRegex.exec(url)) !== null) {
				let loc = new NavLocation(NavType.Configuration /*NavType.Dashboard*/);
				this.selectLocation(loc);
			}
			else if ((result = this.configurationRegex.exec(url)) !== null) {
				let loc = new NavLocation(NavType.Configuration);
				this.selectLocation(loc);
			}
			else if ((result = this.networkRegex.exec(url)) !== null) {
				let networkGuid = result[1];
				let network: NetViewDto = null;

				network = this.netFacadeService.findNetwork(networkGuid);
				if (network) {
					let loc = new NavLocation(NavType.Network);
					loc.netViewGuid = networkGuid;
					this.selectLocation(loc);
				}
				else {
					console.error("Could not find the requested network: " + networkGuid);
					this.gotoDashboard();
				}
			}
			else if ((result = this.agentRegex.exec(url)) !== null) {
				let agentGuid = result[1];
				let agent: AgentDto = null;
				let network: NetViewDto = null;

				agent = this.netFacadeService.findAgent(agentGuid);
				if (agent) {
					network = this.netFacadeService.findNetwork(agent.ScopeGuid);
				}

				if (network && agent) {
					let loc = new NavLocation(NavType.Agent);
					loc.netViewGuid = agent.ScopeGuid;
					loc.agentGuid = agentGuid;
					this.selectLocation(loc);
				}
				else {
					console.error("Could not find the requested agent: " + agentGuid);
					this.gotoDashboard();
				}
			}
			else if ((result = this.discoveryRegex.exec(url)) !== null) {
				let schedDiscGuid = result[1];
				let agent: AgentDto = null;
				let network: NetViewDto = null;

				agent = this.netFacadeService.findSchedDiscParent(schedDiscGuid);
				if (agent) {
					network = this.netFacadeService.findNetwork(agent.ScopeGuid);
				}

				if (network && agent) {
					let loc = new NavLocation(NavType.Discovery);
					loc.netViewGuid = agent.ScopeGuid;
					loc.agentGuid = agent.Guid;
					loc.schedDiscGuid = schedDiscGuid;
					this.selectLocation(loc);
				}
				else {
					console.error("Could not find the requested discovery: " + schedDiscGuid);
					this.gotoDashboard();
				}
			}
			else if ((result = this.discresultRegex.exec(url)) !== null) {
				let schedDiscRsltGuid = result[1];
				this.discResultsService.getSchedDiscResultByGuid(schedDiscRsltGuid, "none").subscribe(
					(result: ScheduledDiscoveryResultDto) => {
						let agent: AgentDto = null;
						let network: NetViewDto = null;

						agent = this.netFacadeService.findSchedDiscParent(result.SchedDiscGuid);
						if (agent) {
							network = this.netFacadeService.findNetwork(agent.ScopeGuid);
						}

						if (network && agent) {
							let loc = new NavLocation(NavType.DiscResult);
							loc.netViewGuid = agent.ScopeGuid;
							loc.agentGuid = agent.Guid;
							loc.schedDiscGuid = result.SchedDiscGuid;
							loc.schedDiscRsltGuid = schedDiscRsltGuid;
							this.selectLocation(loc);
						}
						//else {
						//	console.error("Could not find the requested discovery result: " + schedDiscRsltGuid);
						//	this.gotoDashboard();
						//}
					},
					(error?) => {
						console.error("Could not find the requested discovery result: " + schedDiscRsltGuid);
						this.gotoDashboard();
					}
				);
			}
			else {
				let loc = new NavLocation(NavType.Configuration /*NavType.Dashboard*/);
				this.selectLocation(loc);
			}
		}
	}

	private getLocationUrl(): string {
		switch (this.location.type) {
			case NavType.None:
				return "";
			case NavType.Login:
				return "/login";
			case NavType.Admin:
				return "/admin";
			//case NavType.Tools:
			//	return "/tools";
			case NavType.Account:
				return "/account";
			case NavType.Configuration:
				return "/networks";
			case NavType.Network:
				return "/network/" + this.location.netViewGuid;
			case NavType.Agent:
				return "/agent/" + this.location.agentGuid;
			case NavType.Discovery:
				return "/discovery/" + this.location.schedDiscGuid;
			case NavType.DiscResult:
				return "/discresult/" + this.location.schedDiscRsltGuid;
			default:
				throw new Error("Invalid NavType");
		}
	}

	private initialize(): void {
		this.selectLocation(this.location);
	}

	public selectUrl(url: string): void {
		//console.info("selectUrl: " + url);
		this.router.navigateByUrl(url);
	}

	public gotoNavDestination(): void {
		this.selectUrl(this.navDestination);
		this.navDestination = "";

		//let _gotoNavDestination = (): void => {
		//	this.selectUrl(this.navDestination);
		//	this.navDestination = "";
		//}

		//if (this.netFacadeService.isNetViewTreeLoaded && this.netFacadeService.areSharesLoaded) {
		//	_gotoNavDestination();
		//}
		//else {
		//	let netViewTreeSub: Subscription = null;
		//	let sharesSub: Subscription = null;

		//	let _unsubscribeAndGotoNavDestination = (): void => {
		//		netViewTreeSub.unsubscribe();
		//		sharesSub.unsubscribe();

		//		_gotoNavDestination();
		//	}

		//	netViewTreeSub = this.netFacadeService.onNetViewTreeLoaded.subscribe(
		//		() => {
		//			if (this.netFacadeService.isNetViewTreeLoaded && this.netFacadeService.areSharesLoaded) {
		//				_unsubscribeAndGotoNavDestination();
		//			}
		//		},
		//		(error) => {
		//			_unsubscribeAndGotoNavDestination();
		//		});
		//	sharesSub = this.netFacadeService.onSharesLoaded.subscribe(
		//		() => {
		//			if (this.netFacadeService.isNetViewTreeLoaded && this.netFacadeService.areSharesLoaded) {
		//				_unsubscribeAndGotoNavDestination();
		//			}
		//		},
		//		(error) => {
		//			_unsubscribeAndGotoNavDestination();
		//		});
		//}
	}

	public gotoHome(): void {
		this.selectUrl("/");
	}

	public selectItem(item: NavItem): void {
		if (this.isValidItem(this.levels, item)) {
			// Construct NavLocation that corresponds to the item being selected
			let newLoc = new NavLocation(item.type);

			if (item.type == NavType.Network || item.type == NavType.Agent ||
				item.type == NavType.Discovery || item.type == NavType.DiscResult) {

				// Preserve all current selections above item's level
				for (let i = 0; i < item.levelIndex; ++i) {
					switch (i) {
						case 0:
							newLoc.netViewGuid = this.getSelectedLevelItem(this.levels, 0).guid;
							break;
						case 1:
							newLoc.agentGuid = this.getSelectedLevelItem(this.levels, 1).guid;
							break;
						case 2:
							newLoc.schedDiscGuid = this.getSelectedLevelItem(this.levels, 2).guid;
							break;
						case 3:
							newLoc.schedDiscRsltGuid = this.getSelectedLevelItem(this.levels, 3).guid;
							break;
						default:
							console.assert(false);
							break;
					}
				}

				// Select item itself
				switch (item.levelIndex) {
					case 0:
						newLoc.netViewGuid = item.guid;
						break;
					case 1:
						newLoc.agentGuid = item.guid;
						break;
					case 2:
						newLoc.schedDiscGuid = item.guid;
						break;
					case 3:
						newLoc.schedDiscRsltGuid = item.guid;
						break;
					default:
						console.assert(false);
						break;
				}
			}
			else {
				throw new Error("Invalid NavType");
			}

			this.selectLocation(newLoc);
		}
		else {
			console.log("Cannot select invalid NavItem");
		}
	}

	private isValidItem(levels: NavLevel[], item: NavItem): boolean {
		for (let i = 0; i < this.levels.length; ++i) {
			let level = this.levels[i];

			for (let j = 0; j < level.items.length; ++j) {
				if (level.items[j] === item) {
					return true;
				}
			}
		}
		return false;
	}

	public selectLocation(loc: NavLocation): void {
		if (this.doingSelect) {
			console.log("Skipping reentrant location selection: " + JSON.stringify(loc));
			return;
		}
		else {
			// Prevent re-entry
			this.doingSelect = true;
		}

		if (loc.type == NavType.Login || loc.type == NavType.Admin || /*loc.type == NavType.Tools ||*/
			loc.type == NavType.Account || loc.type == NavType.Configuration /* || loc.type == NavType.Dashboard */) {

			let item = new NavItem();
			item.type = loc.type;
			switch (loc.type) {
				case NavType.Login:
					item.label = "Login";
					break;
				case NavType.Admin:
					item.label = "Admin";
					break;
				//case NavType.Tools:
				//	item.label = "Tools";
				//	break;
				case NavType.Account:
					item.label = "Account";
					break;
				case NavType.Configuration:
					item.label = "Networks";
					break;
				//case NavType.Dashboard:
				//	item.label = "Dashboard";
				//	break;
				default:
					throw new Error("Invalid NavType");
			}
			item.levelIndex = 0;
			item.selected = true;

			let level = new NavLevel();
			level.type = loc.type;
			level.index = 0;
			level.title = NavType[loc.type];
			level.items = [item];

			let newLevels: NavLevel[] = [level];

			this.setLocation(loc, newLevels);
			this.doingSelect = false;
		}
		else if (loc.type == NavType.Network || loc.type == NavType.Agent ||
			loc.type == NavType.Discovery || loc.type == NavType.DiscResult) {

			let newLoc = new NavLocation();

			let newLevels: NavLevel[] = [];

			this.addNetworkLevel(newLevels);

			if (loc.hasNetViewGuid) {
				if (this.selectLevelItem(newLevels, 0, loc.netViewGuid)) {
					newLoc.netViewGuid = loc.netViewGuid;
				}
			}

			if (newLoc.hasNetViewGuid) {
				this.addAgentLevel(newLevels, newLoc.netViewGuid);

				if (loc.hasAgentGuid) {
					if (this.selectLevelItem(newLevels, 1, loc.agentGuid)) {
						newLoc.agentGuid = loc.agentGuid;
					}
				}
			}

			if (newLoc.hasAgentGuid) {
				this.addSchedDiscLevel(newLevels, newLoc.agentGuid);

				if (loc.hasSchedDiscGuid) {
					if (this.selectLevelItem(newLevels, 2, loc.schedDiscGuid)) {
						newLoc.schedDiscGuid = loc.schedDiscGuid;
					}
				}
			}

			// Since we may need to fetch the discovery results for the selected discovery,
			// and this is done asynchronously, we must wait to complete our algorithm
			// until the observable returns a result.  For uniformity, the cases that do not
			// require the asynchronous web API call are also implemented with an Observable
			// (this was the best way to keep the code as clear as possible)

			let observable: Observable<List<ScheduledDiscoveryResultDto>>;
			if (newLoc.hasSchedDiscGuid) {
				observable = this.discResultsService.getSchedDiscResults(newLoc.schedDiscGuid, "none");
			}
			else {
				observable = new Observable<List<ScheduledDiscoveryResultDto>>(observer => {
					observer.next(null);
				});
			}

			observable.subscribe(
				(result: List<ScheduledDiscoveryResultDto>) => {
					if (newLoc.hasSchedDiscGuid) {
						if (result != null) {
							this.addSchedDiscRsltLevel(newLevels, result);

							if (loc.hasSchedDiscRsltGuid) {
								if (this.selectLevelItem(newLevels, 3, loc.schedDiscRsltGuid)) {
									newLoc.schedDiscRsltGuid = loc.schedDiscRsltGuid;
								}
							}
						}
						else {
							console.log("Failed to retrieve discovery results");
						}
					}

					newLoc.deriveType();
					if (newLoc.type == NavLocation.DefaultType) {
						newLevels = this.buildDefaultLevels();
					}

					if (!newLoc.equals(loc)) {
						console.log("Selected item not found.  Navigating to closest related item.");
					}

					this.setLocation(newLoc, newLevels);
					this.doingSelect = false;
				},
				(error?) => {
					console.log("Failed to retrieve discovery results");
					console.log(error);

					newLoc.deriveType();
					if (newLoc.type == NavLocation.DefaultType) {
						newLevels = this.buildDefaultLevels();
					}

					if (!newLoc.equals(loc)) {
						console.log("Selected item not found.  Navigating to closest related item.");
					}

					this.setLocation(newLoc, newLevels);
					this.doingSelect = false;
				}
			);
		}
		else {
			this.doingSelect = false;
			throw new Error("Invalid NavType");
		}
	}

	private buildDefaultLevels(): NavLevel[] {
		let item = new NavItem();
		item.type = NavType.Configuration; //NavType.Dashboard;
		item.label = "Networks"; //"Dashboard";
		item.levelIndex = 0;
		item.selected = true;

		let level = new NavLevel();
		level.type = NavType.Configuration; //NavType.Dashboard;
		level.index = 0;
		level.title = NavType[level.type];
		level.items = [item];

		return [level];
	}

	private setLocation(newLoc: NavLocation, newLevels: NavLevel[]): void {
		switch (newLoc.type) {
			case NavType.Login:
				++this.ignoreRouterEvents;
				//console.info("navigateByUrl: /login");
				this.router.navigateByUrl("/login")
					.then((value: boolean) => {
						--this.ignoreRouterEvents;
						if (value === true || this.navSkipped) {
							this.storeLocation(newLoc, newLevels);
							if (!!this.sessionGuid) {
								this.discResultsService.unload(this.sessionGuid);
							}
						}
					});
				break;
			case NavType.Admin:
				++this.ignoreRouterEvents;
				//console.info("navigateByUrl: /admin");
				this.router.navigateByUrl("/admin")
					.then((value: boolean) => {
						--this.ignoreRouterEvents;
						if (value === true || this.navSkipped || this.location.type == 0) {
							this.storeLocation(newLoc, newLevels);
							if (!!this.sessionGuid) {
								this.discResultsService.unload(this.sessionGuid);
							}
						}
					});
				break;
			case NavType.Account:
				++this.ignoreRouterEvents;
				//console.info("navigateByUrl: /account");
				this.router.navigateByUrl("/account")
					.then((value: boolean) => {
						--this.ignoreRouterEvents;
						if (value === true || this.navSkipped || this.location.type == 0) {
							this.storeLocation(newLoc, newLevels);
							if (!!this.sessionGuid) {
								this.discResultsService.unload(this.sessionGuid);
							}
						}
					});
				break;
			case NavType.Configuration:
				++this.ignoreRouterEvents;
				//console.info("navigateByUrl: /networks");
				this.router.navigateByUrl("/networks")
					.then((value: boolean) => {
						--this.ignoreRouterEvents;
						if (value === true || this.navSkipped || this.location.type == 0) {
							this.storeLocation(newLoc, newLevels);
							if (!!this.sessionGuid) {
								this.discResultsService.unload(this.sessionGuid);
							}
						}
					});
				break;
			case NavType.Network:
				++this.ignoreRouterEvents;
				this.router.navigateByUrl("/network/" + newLoc.netViewGuid)
					.then((value: boolean) => {
						--this.ignoreRouterEvents;
						if (value === true || this.navSkipped || this.location.type == 0) {
							this.storeLocation(newLoc, newLevels);
							this.loadNetwork();
						}
					});
				break;
			case NavType.Agent:
				++this.ignoreRouterEvents;
				//console.info("navigateByUrl: /agent/" + newLoc.agentGuid);
				this.router.navigateByUrl("/agent/" + newLoc.agentGuid)
					.then((value: boolean) => {
						--this.ignoreRouterEvents;
						if (value === true || this.navSkipped || this.location.type == 0) {
							this.storeLocation(newLoc, newLevels);
							this.loadNetwork();
						}
					});
				break;
			case NavType.Discovery:
				++this.ignoreRouterEvents;
				//console.info("navigateByUrl: /discovery/" + newLoc.schedDiscGuid);
				this.router.navigateByUrl("/discovery/" + newLoc.schedDiscGuid)
					.then((value: boolean) => {
						--this.ignoreRouterEvents;
						if (value === true || this.navSkipped || this.location.type == 0) {
							this.storeLocation(newLoc, newLevels);
							this.loadNetwork();
						}
					});
				break;
			case NavType.DiscResult:
				++this.ignoreRouterEvents;
				//console.info("navigateByUrl: /discresult/" + newLoc.schedDiscRsltGuid);
				this.router.navigateByUrl("/discresult/" + newLoc.schedDiscRsltGuid)
					.then((value: boolean) => {
						--this.ignoreRouterEvents;
						if (value === true || this.navSkipped || this.location.type == 0) {
							this.storeLocation(newLoc, newLevels);
							this.loadNetwork();
						}
					});
				break;
			default:
				throw new Error("Invalid NavType");
		}
	}

	private loadNetwork(): void {
		let sessionGuid = this.sessionGuid;
		let netViewGuid = this.location.netViewGuid;
		let agentGuid = this.location.agentGuid;
		let schedDiscGuid = this.location.schedDiscGuid;
		let schedDiscRsltGuid = this.location.schedDiscRsltGuid;

		this.discResultsService.loadNetwork(sessionGuid, netViewGuid, agentGuid,
			schedDiscGuid, schedDiscRsltGuid).subscribe(
				(result: boolean) => {
					//this.messageService.displayInfoMessage("Successfully loaded network!");
				},
				(error?) => {
					this.messageService.displayErrorMessage("Could not load network");
				});
	}

	private storeLocation(newLoc: NavLocation, newLevels: NavLevel[]): void {
		this.location = newLoc;
		this.levels = newLevels;

		this.locationChanged.next(true);
		this.levelsChanged.next(true);
	}

	public gotoLogin(/*navDest: string*/): void {
		//this.navDestination = navDest;
		this.selectLocation(new NavLocation(NavType.Login));
	}

	public gotoAdmin(): void {
		this.selectLocation(new NavLocation(NavType.Admin));
	}

	//public gotoTools(): void {
	//	this.selectLocation(new NavLocation(NavType.Tools));
	//}

	public gotoAccount(): void {
		this.selectLocation(new NavLocation(NavType.Account));
	}

	public gotoDashboard(): void {
		this.selectLocation(new NavLocation(NavType.Configuration /*NavType.Dashboard*/));
	}

	public gotoConfiguration(): void {
		this.selectLocation(new NavLocation(NavType.Configuration));
	}

	private addNetworkLevel(levels: NavLevel[]): void {
		let netLevel = new NavLevel();
		netLevel.type = NavType.Network;
		netLevel.index = 0;
		netLevel.title = "Networks";

		let nets = [];
		if (this.identityService.session.EffectiveUserClass !== UserClass.Observer) {
			this.netFacadeService.networks.forEach((netView: NetViewDto) => {
				nets.push(netView);
			});
		}
		this.netFacadeService.targetNetworks.forEach((netView: NetViewDto) => {
			// Remove duplicate networks
			if (!nets.find((nv: NetViewDto) => nv.Guid === netView.Guid)) {
				nets.push(netView);
			}
		});

		nets.sort((a: NetViewDto, b: NetViewDto) => {
			return a.Name.localeCompare(b.Name);
		});

		nets.forEach((net: NetViewDto) => {
			let navItem = new NavItem();
			navItem.type = NavType.Network;
			navItem.levelIndex = 0;
			navItem.guid = net.Guid;
			navItem.label = net.Name;
			navItem.selected = false;
			navItem.tag = net;

			netLevel.items.push(navItem);
		});

		levels.push(netLevel);
	}

	private addAgentLevel(levels: NavLevel[], netViewGuid: string): void {
		let agentLevel = new NavLevel();
		agentLevel.type = NavType.Agent;
		agentLevel.index = 1;
		agentLevel.title = "Agents";

		let netView = this.netFacadeService.findNetwork(netViewGuid);

		let agents = [];
		netView.AgentGuids.ForEach((agentGuid: string) => {
			let agent: AgentDto = this.netFacadeService.findAgent(agentGuid);
			agents.push(agent);
		});

		agents.sort((a: AgentDto, b: AgentDto) => {
			return a.Name.localeCompare(b.Name);
		});

		agents.forEach((agent: AgentDto) => {
			let navItem = new NavItem();
			navItem.type = NavType.Agent;
			navItem.levelIndex = 1;
			navItem.guid = agent.Guid;
			navItem.label = agent.Name;
			navItem.selected = false;
			navItem.tag = agent;

			agentLevel.items.push(navItem);
		});

		levels.push(agentLevel);
	}

	private addSchedDiscLevel(levels: NavLevel[], agentGuid: string): void {
		let schedDiscLevel = new NavLevel();
		schedDiscLevel.type = NavType.Discovery;
		schedDiscLevel.index = 2;
		schedDiscLevel.title = "Discoveries";

		let agent = this.netFacadeService.findAgent(agentGuid);
		let schedDiscs = agent.ScheduledDiscoveries.ToArray();

		schedDiscs.sort((a: ScheduledDiscovery, b: ScheduledDiscovery) => {
			return a.Name.localeCompare(b.Name);
		});

		schedDiscs.forEach((disc: ScheduledDiscovery) => {
			let navItem = new NavItem();
			navItem.type = NavType.Discovery;
			navItem.levelIndex = 2;
			navItem.guid = disc.GUID;
			navItem.label = disc.Name;
			navItem.selected = false;
			navItem.tag = disc;

			schedDiscLevel.items.push(navItem);
		});

		levels.push(schedDiscLevel);
	}

	private addSchedDiscRsltLevel(levels: NavLevel[], results: List<ScheduledDiscoveryResultDto>): void {
		let schedDiscRsltLevel = new NavLevel();
		schedDiscRsltLevel.type = NavType.DiscResult;
		schedDiscRsltLevel.index = 3;
		schedDiscRsltLevel.title = "Discovery Results";

		let schedDiscRslts = results.ToArray();

		schedDiscRslts.sort((a: ScheduledDiscoveryResultDto, b: ScheduledDiscoveryResultDto) => {
			// Sort newest to oldest
			let aDate = new Date(a.Created);
			let bDate = new Date(b.Created);
			return aDate > bDate ? -1 : aDate < bDate ? 1 : 0;
		});

		schedDiscRslts.forEach((result: ScheduledDiscoveryResultDto) => {
			let navItem = new NavItem();
			navItem.type = NavType.DiscResult;
			navItem.levelIndex = 3;
			navItem.guid = result.Guid;
			navItem.label = this.dateService.formatDateHm(result.EndTime);
			navItem.selected = false;
			navItem.tag = result;

			schedDiscRsltLevel.items.push(navItem);
		});

		levels.push(schedDiscRsltLevel);
	}

	private selectLevelItem(levels: NavLevel[], levelIndex: number, guid: string): boolean {
		let found = false;

		levels[levelIndex].items.forEach((item: NavItem) => {
			if (item.guid === guid) {
				item.selected = true;
				found = true;
			}
			else {
				item.selected = false;
			}
		});

		return found;
	}

	public getSelectedLevelItem(levels: NavLevel[], levelIndex: number): NavItem {
		let level = levels[levelIndex];
		for (let i = 0; i < level.items.length; ++i) {
			let item = level.items[i];
			if (item.selected) {
				return item;
			}
		}
		return null;
	}

	public get isNetworkLoaded(): boolean {
		if (this.location.hasNetViewGuid) {
			let netViewGuid: string = this.location.netViewGuid;
			let network: NetViewDto = this.netFacadeService.findNetwork(netViewGuid)
			if (network) {
				return true;
			}
		}
		return false;
	}

}